import { datadogLogs } from "@datadog/browser-logs";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { sleep } from "@redotech/util/schedule";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

export interface UsePollUpdatesProps {
  groups: FulfillmentGroupData[];
  onGroupsUpdated: (groups: FulfillmentGroupData[]) => void;
  enabled?: boolean;
  /** How long to wait between poll checks. */
  pollInterval?: Temporal.Duration;
  /** How long a group must be pending before it is polled. */
  pendingThreshold?: Temporal.Duration;
}

const DEFAULT_POLL_INTERVAL = Temporal.Duration.from({ seconds: 2 });
const DEFAULT_PENDING_THRESHOLD = Temporal.Duration.from({ seconds: 10 });

function deduplicatedGroups(groups: FulfillmentGroupData[]) {
  const uniqueGroupsMap = new Map();

  for (const group of groups) {
    uniqueGroupsMap.set(group._id, group);
  }

  return Array.from(uniqueGroupsMap.values());
}

/**
 * Hook for polling fulfillment groups with pending rates
 * Automatically polls groups that have ratesPendingSince older than
 * pendingThreshold (10 seconds by default)
 */
export function usePollUpdates({
  groups,
  onGroupsUpdated,
  enabled = true,
  pollInterval = DEFAULT_POLL_INTERVAL,
  pendingThreshold = DEFAULT_PENDING_THRESHOLD,
}: UsePollUpdatesProps): void {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useRequiredContext(TeamContext);

  useLoad(
    async (signal) => {
      if (!rpcClient || !team || !enabled) {
        return;
      }

      let errorCount = 0;
      while (true) {
        try {
          // Always wait pollInterval at the start of each loop iteration
          await sleep(pollInterval, signal);

          const pendingCutoff =
            Date.now() - pendingThreshold.total("milliseconds");
          const groupsToPoll = groups.filter(
            (group) =>
              (group.ratesPendingSince &&
                group.ratesPendingSince.getTime() < pendingCutoff) ||
              (group.personalizedSlipGeneratingSince &&
                group.personalizedSlipGeneratingSince.getTime() <
                  pendingCutoff),
          );

          logMissedUpdates(groupsToPoll);

          // Only make the API call if there are groups that need polling
          if (groupsToPoll.length > 0) {
            const response = await rpcClient.getReplacementGroups({
              groupIds: groupsToPoll.map(({ _id }) => _id),
            });

            const newGroups = deduplicatedGroups(
              response.replacements.map(
                (replacement) => replacement.replacementGroup,
              ),
            );

            const resolved = newGroups.filter(
              (group) => !group.ratesPendingSince,
            );
            if (resolved.length > 0) {
              datadogLogs.logger.info("Polling found group rates updates", {
                count: resolved.length,
              });
            }

            if (newGroups.length > 0) {
              onGroupsUpdated(newGroups);
            }
          }

          errorCount = 0;
        } catch (e) {
          if (signal.aborted) {
            break;
          }
          errorCount++;
          console.error("Error while polling for rate updates", e);
          await sleep(
            Temporal.Duration.from({ seconds: Math.min(errorCount, 5) }),
            signal,
          );
        }
      }
    },
    [rpcClient, team, groups, enabled, pollInterval, pendingThreshold],
  );
}

function logMissedUpdates(groups: FulfillmentGroupData[]) {
  const { maxLatencyGroupId, maxRatesPendingSinceMilliseconds } = groups.reduce(
    (max, group) => {
      const ratesPendingSinceMilliseconds = group.ratesPendingSince
        ? Date.now() - group.ratesPendingSince.getTime()
        : 0;
      if (
        ratesPendingSinceMilliseconds > max.maxRatesPendingSinceMilliseconds
      ) {
        return {
          maxLatencyGroupId: group._id,
          maxRatesPendingSinceMilliseconds: ratesPendingSinceMilliseconds,
        };
      }
      return max;
    },
    { maxLatencyGroupId: "", maxRatesPendingSinceMilliseconds: 0 },
  );

  // This number is just preventing log spam. The number we care about should be set in the monitor
  if (maxRatesPendingSinceMilliseconds > 15_000) {
    console.error(
      `ratesPendingSince latency: fulfillmentGroupId ${maxLatencyGroupId} latency ${maxRatesPendingSinceMilliseconds}`,
    );
  }
}
